import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useApp } from "../contexts/AppContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { 
  ChevronLeft, ChevronRight, Download, Copy, ClipboardPaste, Pencil, Trash2, Plus, X
} from "lucide-react";
import { 
  MONTH_NAMES, STATUS_COLORS, getDaysInMonth, calculateHours, API_URL
} from "../lib/utils";


export default function SchedulesPage() {
  const { companies, locations, fetchCompanies, fetchLocations } = useApp();
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  
  // Edit dialog state
  const [editDialog, setEditDialog] = useState(null); // { employeeId, day }
  const [editForm, setEditForm] = useState({ 
    status: "lavoro", start_time: "08:00", end_time: "16:00",
    start_time_2: "", end_time_2: "", hasSplit: false
  });

  // Drag-to-fill state
  const [dragState, setDragState] = useState(null);
  const dragRef = useRef(null);

  // Clipboard for copy/paste
  const [clipboard, setClipboard] = useState(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const longPressRef = useRef(null);
  const longPressFired = useRef(false);

  useEffect(() => {
    fetchCompanies();
    fetchLocations();
  }, [fetchCompanies, fetchLocations]);

  useEffect(() => {
    if (filterLocation) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, filterLocation]);

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const loadData = async () => {
    if (!filterLocation) return;
    setLoading(true);
    try {
      const [empRes, schedRes] = await Promise.all([
        axios.get(`${API_URL}/api/employees`, { params: { location_id: filterLocation }, withCredentials: true }),
        axios.get(`${API_URL}/api/schedules`, { params: { year, month, location_id: filterLocation }, withCredentials: true })
      ]);
      setEmployees(empRes.data);
      const schedMap = {};
      schedRes.data.forEach(s => { schedMap[s.employee_id] = s; });
      setSchedules(schedMap);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else { setMonth(month - 1); }
  };
  const handleNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else { setMonth(month + 1); }
  };

  // Compute total hours for a day (including split shift)
  const computeHours = (form) => {
    let h = form.status === "lavoro" ? calculateHours(form.start_time, form.end_time) : 0;
    if (form.hasSplit && form.start_time_2 && form.end_time_2) {
      h += calculateHours(form.start_time_2, form.end_time_2);
    }
    return h;
  };

  // Open edit dialog for a cell
  const openEditDialog = (employeeId, day) => {
    const schedule = schedules[employeeId];
    const dayData = schedule?.days?.[day.toString()] || {};
    const hasSplit = !!(dayData.start_time_2 && dayData.end_time_2);
    setEditForm({
      status: dayData.status || "lavoro",
      start_time: dayData.start_time || "08:00",
      end_time: dayData.end_time || "16:00",
      start_time_2: dayData.start_time_2 || "",
      end_time_2: dayData.end_time_2 || "",
      hasSplit
    });
    setEditDialog({ employeeId, day: day.toString() });
    setContextMenu(null);
  };

  // Save cell from dialog
  const handleSaveCell = async () => {
    if (!editDialog) return;
    setSaving(true);
    const { employeeId, day } = editDialog;
    const hours = computeHours(editForm);
    const isWork = editForm.status === "lavoro";
    const currentSchedule = schedules[employeeId] || { days: {} };
    const updatedDays = {
      ...currentSchedule.days,
      [day]: {
        status: editForm.status,
        start_time: isWork ? editForm.start_time : null,
        end_time: isWork ? editForm.end_time : null,
        start_time_2: isWork && editForm.hasSplit ? editForm.start_time_2 : null,
        end_time_2: isWork && editForm.hasSplit ? editForm.end_time_2 : null,
        hours
      }
    };
    try {
      const response = await axios.post(`${API_URL}/api/schedules`, {
        employee_id: employeeId, year, month, days: updatedDays
      }, { withCredentials: true });
      setSchedules(prev => ({ ...prev, [employeeId]: response.data }));
      setEditDialog(null);
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Save multiple days in background
  const saveDaysInBackground = useCallback(async (employeeId, daysToSave) => {
    const currentSchedule = schedules[employeeId] || { days: {} };
    const updatedDays = { ...currentSchedule.days };
    daysToSave.forEach(({ day, data }) => { updatedDays[day.toString()] = data; });
    try {
      const response = await axios.post(`${API_URL}/api/schedules`, {
        employee_id: employeeId, year, month, days: updatedDays
      }, { withCredentials: true });
      setSchedules(prev => ({ ...prev, [employeeId]: response.data }));
    } catch (err) {
      console.error("Background save error:", err);
    }
  }, [schedules, year, month]);

  // Delete a cell
  const handleDeleteCell = (employeeId, day) => {
    const currentSchedule = schedules[employeeId] || { days: {} };
    const updatedDays = { ...currentSchedule.days };
    delete updatedDays[day.toString()];
    axios.post(`${API_URL}/api/schedules`, {
      employee_id: employeeId, year, month, days: updatedDays
    }, { withCredentials: true }).then(response => {
      setSchedules(prev => ({ ...prev, [employeeId]: response.data }));
    }).catch(err => console.error("Delete error:", err));
    setContextMenu(null);
  };

  // === DRAG TO FILL ===
  const handleDragStart = (e, employeeId, day) => {
    e.preventDefault();
    e.stopPropagation();
    const schedule = schedules[employeeId];
    const dayData = schedule?.days?.[day.toString()];
    if (!dayData || !dayData.status) return;
    const cellData = { ...dayData };
    dragRef.current = { employeeId, startDay: day, currentDay: day, cellData };
    setDragState({ employeeId, startDay: day, currentDay: day, cellData });

    const handleMouseMove = (ev) => {
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      if (!el) return;
      const cell = el.closest("[data-day]");
      if (cell && cell.dataset.empid === employeeId) {
        const newDay = parseInt(cell.dataset.day);
        if (dragRef.current && newDay !== dragRef.current.currentDay) {
          dragRef.current.currentDay = newDay;
          setDragState(prev => prev ? { ...prev, currentDay: newDay } : null);
        }
      }
    };
    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      const state = dragRef.current;
      if (state && state.startDay !== state.currentDay) {
        const minDay = Math.min(state.startDay, state.currentDay);
        const maxDay = Math.max(state.startDay, state.currentDay);
        const daysToSave = [];
        for (let d = minDay; d <= maxDay; d++) {
          if (d === state.startDay) continue;
          daysToSave.push({ day: d, data: { ...state.cellData } });
        }
        if (daysToSave.length > 0) saveDaysInBackground(state.employeeId, daysToSave);
      }
      dragRef.current = null;
      setDragState(null);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // === CONTEXT MENU ===
  const handleContextMenu = (e, employeeId, day) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, employeeId, day: day.toString() });
  };

  const handleCopy = () => {
    if (!contextMenu) return;
    const schedule = schedules[contextMenu.employeeId];
    const dayData = schedule?.days?.[contextMenu.day];
    if (dayData && dayData.status) setClipboard({ ...dayData });
    setContextMenu(null);
  };

  const handlePaste = () => {
    if (!contextMenu || !clipboard) return;
    saveDaysInBackground(contextMenu.employeeId, [{
      day: parseInt(contextMenu.day),
      data: { ...clipboard }
    }]);
    setContextMenu(null);
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/export/schedule/${year}/${month}`, {
        params: { location_id: filterLocation }, responseType: 'blob', withCredentials: true
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orario_${year}_${month.toString().padStart(2, '0')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Errore nell'export: " + (err.response?.data?.detail || err.message));
    }
  };

  const daysInMonth = getDaysInMonth(year, month);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const filteredLocations = filterCompany ? locations.filter(l => l.company_id === filterCompany) : locations;

  // Detect if any cell in the grid has a split shift for uniform row height
  const hasSplitInGrid = useMemo(() => {
    return employees.some(emp => {
      const schedule = schedules[emp.id];
      if (!schedule?.days) return false;
      return Object.values(schedule.days).some(d => d.start_time_2 && d.end_time_2);
    });
  }, [employees, schedules]);

  const cellHeightClass = hasSplitInGrid ? "h-[72px]" : "h-10";

  const hasCellData = (employeeId, day) => {
    const schedule = schedules[employeeId];
    const dayData = schedule?.days?.[day.toString()];
    return dayData && dayData.status;
  };

  const getCellContent = (employeeId, day) => {
    const schedule = schedules[employeeId];
    const dayData = schedule?.days?.[day.toString()];
    if (!dayData || !dayData.status) return null;
    const color = STATUS_COLORS[dayData.status];
    const hasSplit = dayData.start_time_2 && dayData.end_time_2;
    return (
      <div 
        className="w-full h-full flex flex-col items-center justify-center text-[10px] leading-tight font-ibm-plex-mono"
        style={{ backgroundColor: color?.bg, color: color?.text }}
      >
        {dayData.status === "lavoro" ? (
          <>
            <span className="font-semibold text-[11px]">{dayData.hours || 0}h</span>
            {dayData.start_time && dayData.end_time && (
              <>
                <span className="opacity-80 text-[8px]">{dayData.start_time}</span>
                <span className="opacity-80 text-[8px]">{dayData.end_time}</span>
              </>
            )}
            {hasSplit && (
              <>
                <span className="opacity-80 text-[8px]">{dayData.start_time_2}</span>
                <span className="opacity-80 text-[8px]">{dayData.end_time_2}</span>
              </>
            )}
          </>
        ) : (
          <span className="text-[9px] font-medium">{color?.cellLabel || color?.label}</span>
        )}
      </div>
    );
  };

  const getTotalHours = (employeeId) => schedules[employeeId]?.total_hours || 0;

  const isDragHighlighted = (employeeId, day) => {
    if (!dragState || dragState.employeeId !== employeeId) return false;
    const min = Math.min(dragState.startDay, dragState.currentDay);
    const max = Math.max(dragState.startDay, dragState.currentDay);
    return day >= min && day <= max && day !== dragState.startDay;
  };

  return (
    <div className="space-y-6" data-testid="schedules-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit tracking-tight text-[#09090B]">Orario Mensile</h1>
          <p className="text-[#52525B] font-ibm-plex mt-1">Gestisci i turni dei dipendenti</p>
        </div>
        {filterLocation && (
          <Button data-testid="export-schedule-pdf" onClick={handleExportPDF} variant="outline" className="font-ibm-plex">
            <Download className="w-4 h-4 mr-2" /> Esporta PDF
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border border-[#E4E4E7]">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
            <Select value={filterCompany || "all"} onValueChange={(v) => { setFilterCompany(v === "all" ? "" : v); setFilterLocation(""); }}>
              <SelectTrigger data-testid="schedule-filter-company" className="font-ibm-plex"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le aziende</SelectItem>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterLocation || "none"} onValueChange={(v) => setFilterLocation(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="schedule-filter-location" className="font-ibm-plex"><SelectValue placeholder="Seleziona sede" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Seleziona sede...</SelectItem>
                {filteredLocations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevMonth} data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></Button>
              <div className="flex-1 text-center font-ibm-plex font-medium">{MONTH_NAMES[month]} {year}</div>
              <Button variant="outline" size="icon" onClick={handleNextMonth} data-testid="next-month"><ChevronRight className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs font-ibm-plex max-w-xs">
              {Object.entries(STATUS_COLORS).map(([key, val]) => (
                <div key={key} className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: val.bg }} />
                  <span>{val.label}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clipboard indicator */}
      {clipboard && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F4F4F5] border border-[#E4E4E7] rounded-md text-xs font-ibm-plex text-[#52525B]">
          <ClipboardPaste className="w-3 h-3" />
          <span>
            Copiato: <strong>{STATUS_COLORS[clipboard.status]?.label || clipboard.status}</strong>
            {clipboard.status === "lavoro" && clipboard.start_time && ` ${clipboard.start_time}-${clipboard.end_time}`}
            {clipboard.start_time_2 && clipboard.end_time_2 && ` + ${clipboard.start_time_2}-${clipboard.end_time_2}`}
          </span>
          <button onClick={() => setClipboard(null)} className="ml-auto text-[#71717A] hover:text-[#09090B]">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Schedule Grid */}
      {!filterLocation ? (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="py-12 text-center text-[#52525B] font-ibm-plex">Seleziona una sede per visualizzare l'orario</CardContent>
        </Card>
      ) : loading ? (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="py-12 text-center text-[#52525B] font-ibm-plex">Caricamento...</CardContent>
        </Card>
      ) : employees.length === 0 ? (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="py-12 text-center text-[#52525B] font-ibm-plex">Nessun dipendente trovato</CardContent>
        </Card>
      ) : (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="p-0">
            <div className="overflow-x-auto select-none" style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }}>
              <table className="w-full border-collapse text-sm" style={{ touchAction: "pan-x pan-y" }}>
                <thead>
                  <tr className="bg-[#F4F4F5]">
                    <th className="sticky left-0 bg-[#F4F4F5] z-10 px-3 py-2 text-left font-ibm-plex font-semibold border-r border-b border-[#E4E4E7] min-w-[180px]">Dipendente</th>
                    {days.map(day => (
                      <th key={day} className="px-1 py-2 text-center font-ibm-plex font-semibold border-r border-b border-[#E4E4E7] min-w-[40px]">{day}</th>
                    ))}
                    <th className="px-3 py-2 text-center font-ibm-plex font-semibold border-b border-[#E4E4E7] min-w-[60px]">Ore</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map(emp => (
                    <tr key={emp.id} className="hover:bg-[#F4F4F5]/50" data-testid={`schedule-row-${emp.id}`}>
                      <td className="sticky left-0 bg-white z-10 px-3 py-1 font-ibm-plex font-medium border-r border-b border-[#E4E4E7]">
                        {emp.last_name} {emp.first_name}
                      </td>
                      {days.map(day => {
                        const highlighted = isDragHighlighted(emp.id, day);
                        const hasData = hasCellData(emp.id, day);
                        return (
                          <td 
                            key={day}
                            data-day={day}
                            data-empid={emp.id}
                            className={`border-r border-b border-[#E4E4E7] p-0 cursor-pointer relative group ${highlighted ? "ring-2 ring-inset ring-blue-500 bg-blue-50" : "hover:bg-[#E4E4E7]"}`}
                            data-testid={`cell-${emp.id}-${day}`}
                            onClick={() => { if (!longPressFired.current) openEditDialog(emp.id, day); longPressFired.current = false; }}
                            onContextMenu={(e) => { e.preventDefault(); handleContextMenu(e, emp.id, day); }}
                            onTouchStart={(e) => {
                              longPressFired.current = false;
                              const touch = e.touches[0];
                              const tx = touch.clientX, ty = touch.clientY;
                              longPressRef.current = setTimeout(() => {
                                longPressFired.current = true;
                                setContextMenu({ x: tx, y: ty, employeeId: emp.id, day: day.toString() });
                              }, 400);
                            }}
                            onTouchEnd={(e) => { clearTimeout(longPressRef.current); if (longPressFired.current) e.preventDefault(); }}
                            onTouchMove={() => { clearTimeout(longPressRef.current); }}
                          >
                            <div className={`w-full ${cellHeightClass}`}>
                              {getCellContent(emp.id, day)}
                            </div>
                            {hasData && !dragState && (
                              <div
                                className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-[#09090B] cursor-crosshair opacity-0 group-hover:opacity-100 transition-opacity z-20"
                                onMouseDown={(e) => handleDragStart(e, emp.id, day)}
                                onClick={(e) => e.stopPropagation()}
                                title="Trascina per riempire"
                              />
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-1 text-center font-ibm-plex-mono font-medium border-b border-[#E4E4E7]">
                        {getTotalHours(emp.id)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-[100] bg-white border border-[#E4E4E7] rounded-lg shadow-lg py-1 min-w-[150px] font-ibm-plex"
          style={{ 
            left: contextMenu.x, 
            ...(contextMenu.y + 180 > window.innerHeight 
              ? { bottom: window.innerHeight - contextMenu.y } 
              : { top: contextMenu.y })
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {hasCellData(contextMenu.employeeId, parseInt(contextMenu.day)) && (
            <>
              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#F4F4F5] text-left" onClick={() => openEditDialog(contextMenu.employeeId, parseInt(contextMenu.day))} data-testid="context-menu-edit">
                <Pencil className="w-3.5 h-3.5 text-[#52525B]" /> Modifica
              </button>
              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#F4F4F5] text-left" onClick={handleCopy} data-testid="context-menu-copy">
                <Copy className="w-3.5 h-3.5 text-[#52525B]" /> Copia
              </button>
              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-red-50 text-left text-red-600" onClick={() => handleDeleteCell(contextMenu.employeeId, contextMenu.day)} data-testid="context-menu-delete">
                <Trash2 className="w-3.5 h-3.5" /> Elimina
              </button>
            </>
          )}
          {!hasCellData(contextMenu.employeeId, parseInt(contextMenu.day)) && (
            <button className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#F4F4F5] text-left" onClick={() => openEditDialog(contextMenu.employeeId, parseInt(contextMenu.day))} data-testid="context-menu-new">
              <Plus className="w-3.5 h-3.5 text-[#52525B]" /> Inserisci
            </button>
          )}
          {clipboard && (
            <button className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#F4F4F5] text-left border-t border-[#E4E4E7]" onClick={handlePaste} data-testid="context-menu-paste">
              <ClipboardPaste className="w-3.5 h-3.5 text-[#52525B]" /> Incolla
            </button>
          )}
        </div>
      )}

      {/* Edit Cell Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(open) => { if (!open) setEditDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {editDialog && `${employees.find(e => e.id === editDialog.employeeId)?.last_name || ""} — Giorno ${editDialog?.day}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-xs font-ibm-plex font-medium">Stato</label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger className="font-ibm-plex h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lavoro">Lavoro</SelectItem>
                  <SelectItem value="riposo">Riposo</SelectItem>
                  <SelectItem value="ferie">Ferie</SelectItem>
                  <SelectItem value="malattia">Malattia</SelectItem>
                  <SelectItem value="104">104</SelectItem>
                  <SelectItem value="permesso_lutto">Permesso Lutto</SelectItem>
                  <SelectItem value="permesso_matrimoniale">Permesso Matrimoniale</SelectItem>
                  <SelectItem value="rol">ROL</SelectItem>
                  <SelectItem value="congedo_parentale">Congedo Parentale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.status === "lavoro" && (
              <>
                {/* First shift */}
                <div className="space-y-2">
                  <label className="text-xs font-ibm-plex font-medium">Fascia 1</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="time" value={editForm.start_time} onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })} className="font-ibm-plex h-9" data-testid="edit-start-time" />
                    <Input type="time" value={editForm.end_time} onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })} className="font-ibm-plex h-9" data-testid="edit-end-time" />
                  </div>
                </div>

                {/* Second shift toggle */}
                {!editForm.hasSplit ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full font-ibm-plex text-xs"
                    onClick={() => setEditForm({ ...editForm, hasSplit: true, start_time_2: "19:00", end_time_2: "22:00" })}
                    data-testid="add-split-shift"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Aggiungi orario spezzato
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-ibm-plex font-medium">Fascia 2</label>
                      <button
                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                        onClick={() => setEditForm({ ...editForm, hasSplit: false, start_time_2: "", end_time_2: "" })}
                        data-testid="remove-split-shift"
                      >
                        <X className="w-3 h-3" /> Rimuovi
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="time" value={editForm.start_time_2} onChange={(e) => setEditForm({ ...editForm, start_time_2: e.target.value })} className="font-ibm-plex h-9" data-testid="edit-start-time-2" />
                      <Input type="time" value={editForm.end_time_2} onChange={(e) => setEditForm({ ...editForm, end_time_2: e.target.value })} className="font-ibm-plex h-9" data-testid="edit-end-time-2" />
                    </div>
                  </div>
                )}

                {/* Hours preview */}
                <div className="text-center text-sm font-ibm-plex-mono bg-[#F4F4F5] rounded-md py-1.5">
                  Ore totali: <strong>{computeHours(editForm)}</strong>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)} className="font-ibm-plex">Annulla</Button>
            <Button onClick={handleSaveCell} disabled={saving} className="bg-black hover:bg-gray-800 text-white font-ibm-plex" data-testid="save-schedule-cell">
              {saving ? "Salvo..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
